<script setup lang="ts">
import { PromotionBrandProps } from "./PromotionBrand.props"
import CardPromoPromotion from "~/components/Card/Promo/Promotion.vue"
import type { WithContext, Offer } from "schema-dts"
import {
  VIEW_ITEM_LIST_IDS,
  VIEW_ITEM_LIST_NAMES
} from "~/utils/analytics/viewItemList"

const props = defineProps<PromotionBrandProps>()
const route = useRoute()
const aisComponent = ref(null)
const isAisComponentRendered = ref(false)

const serverRendered = useState(`server-rendered-${route.path}`, () => false)
onServerPrefetch(() => {
  serverRendered.value = true
})

watch(
  () => aisComponent.value,
  (newValue) => {
    isAisComponentRendered.value = !!newValue
  },
  { immediate: true }
)

const { setPageInfo } = usePageInfo()

setPageInfo({
  query: "",
  listingType: "promotion",
  filterString: `promotionCategories:"${props.promotionName}"`,
  pageTitle: ""
})

//Structured data of Schema.org, (https://lafarmacia.atlassian.net/wiki/spaces/TF/pages/152305666/Pagine+Promo+Struttura+desiderata)
const jsonLd = computed<WithContext<Offer>>(() => ({
  "@type": "Offer",
  "@context": "https://schema.org",
  availabilityStarts: props.startDate,
  availabilityEnds: props.endDate,
  name: props.promotionName,
  ...(props.brandBanner
    ? {
        offeredBy: {
          "@type": "Organization",
          brand: props.brandBanner?.altText
        },
        image: {
          "@type": "ImageObject",
          image: props.brandBanner?.image.url
        }
      }
    : {}),
  description: props.description
}))

useHead({
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify(cleanJsonLdObject(jsonLd.value))
    }
  ]
})
</script>

<template>
  <div class="grid-standard pb-6 md:pb-10">
    <UtilsBreadcrumbs
      v-bind="breadcrumbs"
      class="negative-margined col-span-full hidden md:block"
    />
    <div
      class="
        col-span-full
        mb-4
        hidden
        overflow-hidden
        md:relative
        md:mb-6
        md:flex
      "
      :class="{ 'md:aspect-[4/1]': !!brandBanner?.image?.url }"
    >
      <UtilsWrapperImage
        v-if="!!brandBanner"
        :="brandBanner"
        :preload="true"
        :img-attrs="{ fetchpriority: 'high' }"
        class="absolute left-0 top-1/2 w-full -translate-y-1/2"
      />
    </div>
    <UtilsWrapperImage
      v-if="!!cardImage"
      class="col-span-full mx-[-16px] pb-4 md:hidden"
      v-bind="cardImage"
      :preload="true"
      :img-attrs="{ fetchpriority: 'high' }"
    />
    <div class="col-span-full">
      <h1 v-if="title" class="hyppo-bold md:mammoth-bold mb-2">{{ title }}</h1>
      <h2 v-if="subtitle" class="pig md:elephant mb-2">{{ subtitle }}</h2>
      <UtilsMarkdown
        v-if="description"
        class="beaver md:pig markdown__promotion-description"
        :content="description"
      />
    </div>

    <div v-if="content" class="col-span-full">
      <DynamicContent class="cms-content mt-3 md:mt-11" :components="content" />
    </div>

    <div v-if="!hasAlgoliaListing" class="negative-margined col-span-full">
      <AlgoliaListingPage>
        <template #products="slotProps">
          <GAListPageList
            :name="VIEW_ITEM_LIST_NAMES.PROMO"
            :id="VIEW_ITEM_LIST_IDS.ON_SALE"
          >
            <GridListCategory
              ref="aisComponent"
              :products-list="slotProps.items"
              :list-name="VIEW_ITEM_LIST_NAMES.PROMO"
              :list-id="VIEW_ITEM_LIST_IDS.ON_SALE"
            />
          </GAListPageList>
        </template>
      </AlgoliaListingPage>
    </div>
    <div class="col-span-full" v-if="suggestedPromotions?.length">
      <p class="hyppo-bold md:elephant-bold mb-6">
        {{ $t("pages.promotion.ourBrandPromotions") }}
      </p>
      <div class="grid-container md:gap-4">
        <CardPromoPromotion
          v-for="promotion in suggestedPromotions"
          :key="promotion.title"
          v-bind="promotion"
          promo-location="top"
          class="col-span-4"
        />
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep(.cms-content) {
  .cmp-discount-rails {
    @apply mx-[-16px] md:mx-[-71px] lg:mx-[-228px];
  }
}
</style>
